// 材料検索の正規化応答(NormalizedMaterialSearchResponse)を、画面が使うネスト形
// (searchedMaterialRows)へ復元する共通ハイドレータ。
// belongsTo / hasMany の id 結合は nestHydrator の宣言的エンジン(生成メタデータのエッジキー)で行い、
// 自動生成できない業務射影(代表施工の選定・消費明細の射影)だけ関数で残す。
//
// User の結び付けは api 側 Sequelize の関連付け(同名 belongsTo の後勝ち)と同じ規則:
//   発行/ロット/出荷/搬入/施工 = created_by_user_id、施工計画/施工記録 = preparer_user_id。
//   工事(Work)は担当者を持たないため User ネストは無く、搬入経由・施工計画経由の2系統とも work_id で解決する。
import {
  ConstructionForMaterialSearch,
  MaterialConsumption,
  NormalizedMaterialSearchResponse,
  searchedMaterial,
  searchedMaterialRows,
} from "../../schema/console/material";
import { getUserType } from "../../schema/console/user";
import { belongsToOf, hasManyOf, mapNullable } from "./nestHydrator";
import { mapById, usersWithGroupOf } from "./relatedLookup";
import { required } from "../typed";

export function hydrateMaterialSearch(normalized: NormalizedMaterialSearchResponse): searchedMaterialRows {
  const related = normalized.related;
  // 汎用ノーマライザは User 内の Group も分離して返すため、共通ヘルパで Group を再結合して従来の getUser 形へ戻す
  const users = usersWithGroupOf(related.Users, related.Groups);
  const issuanceOf = belongsToOf("Material.issuance_id", related.Issuances);
  const lotOf = belongsToOf("Material.lot_id", related.Lots);
  const carryOutOf = belongsToOf("Lot.carry_out_id", related.CarryOuts);
  const carryInOf = belongsToOf("Material.carry_in_id", related.CarryIns);
  const carryInWorkOf = belongsToOf("CarryIn.work_id", related.Works);
  const planWorkOf = belongsToOf("ConstructionPlan.work_id", related.Works);
  const constructionPlanOf = belongsToOf("Construction.construction_plan_id", related.ConstructionPlans);
  const planAreaOf = belongsToOf("ProcessExecution.construction_plan_area_id", related.ConstructionPlanAreas);
  const consumedConstructionOf = belongsToOf("ConstructionMaterialConsumption.construction_id", related.Constructions);
  const recordsOf = hasManyOf("Construction.ConstructionRecords", related.ConstructionRecords);
  const processExecutionRowsOf = hasManyOf("Construction.ProcessExecutions", related.ProcessExecutions);
  const consumptionRowsOf = hasManyOf("Material.ConstructionMaterialConsumptions", related.ConstructionMaterialConsumptions);
  // 【自動生成できない業務射影】代表施工の選定(サーバーが Representatives で示す material→施工の対応)
  const constructions = mapById(related.Constructions);
  const representativeByMaterialId = new Map(normalized.Representatives.map((rep) => [rep.material_id, rep.construction_id]));

  const userOf = (userId: string | null | undefined, context: string): getUserType => {
    if (userId == null) {
      throw new Error(`${context} のユーザーIDが空です(正規化応答の不整合)`);
    }
    return required(users.get(userId), `${context} のユーザー(${userId})`);
  };
  const userOrNull = (userId: string | null | undefined): getUserType | null => {
    if (userId == null) {
      return null;
    }
    return users.get(userId) ?? null;
  };

  // 施工に属する工程実施(+施工箇所)。alive のみサーバーが返す前提(従来ネストと同一)。
  const processExecutionsOf = (constructionId: string) => {
    return processExecutionRowsOf(constructionId).map((processExecution) => ({
      ...processExecution,
      ConstructionPlanArea: planAreaOf(processExecution),
    }));
  };

  // 【自動生成できない業務射影】代表施工のフルネスト(施工計画・施工記録・工程実施・作成者)を組み立てる
  const constructionNestOf = (constructionId: string | null): ConstructionForMaterialSearch => {
    if (constructionId == null) {
      return null;
    }
    const construction = constructions.get(constructionId);
    if (construction == null) {
      return null;
    }
    return {
      ...construction,
      ConstructionPlan: mapNullable(constructionPlanOf(construction), (plan) => ({
        ...plan,
        Work: planWorkOf(plan),
        User: userOf(plan.preparer_user_id, "施工計画"),
      })),
      ConstructionRecords: recordsOf(constructionId).map((record) => ({
        ...record,
        User: userOf(record.preparer_user_id, "施工記録"),
      })),
      ProcessExecutions: processExecutionsOf(constructionId),
      User: userOf(construction.created_by_user_id, "施工"),
    };
  };

  // 【自動生成できない業務射影】QRごとの消費明細(alive のみ・createdAt 昇順)。
  // 参照先施工は削除済みでも deleted_at 付きで返す。
  const consumptionsOf = (materialId: number): MaterialConsumption[] => {
    const rows = [...consumptionRowsOf(materialId)].sort((a, b) => a.createdAt - b.createdAt);
    return rows.map((consumption) => ({
      construction_id: consumption.construction_id,
      used_ratio: consumption.used_ratio,
      createdAt: consumption.createdAt,
      deleted_at: consumption.deleted_at,
      Construction: mapNullable(consumedConstructionOf(consumption), (construction) => ({
        id: construction.id,
        deleted_at: construction.deleted_at,
        ProcessExecutions: processExecutionsOf(construction.id),
        User: userOrNull(construction.created_by_user_id),
      })),
    }));
  };

  return normalized.Materials.map((material): searchedMaterial => ({
    ...material,
    Issuance: mapNullable(issuanceOf(material), (issuance) => ({ ...issuance, User: userOf(issuance.created_by_user_id, "発行") })),
    Lot: mapNullable(lotOf(material), (lot) => ({
      ...lot,
      CarryOut: mapNullable(carryOutOf(lot), (carryOut) => ({ ...carryOut, User: userOf(carryOut.created_by_user_id, "出荷") })),
      User: userOf(lot.created_by_user_id, "ロット"),
    })),
    CarryIn: mapNullable(carryInOf(material), (carryIn) => ({
      ...carryIn,
      Work: carryInWorkOf(carryIn),
      User: userOf(carryIn.created_by_user_id, "搬入"),
    })),
    Construction: constructionNestOf(representativeByMaterialId.get(material.id) ?? null),
    Consumptions: consumptionsOf(material.id),
  }));
}
